import { useCallback, useEffect, useState } from 'react';

import { useAppDispatch } from '@hooks/index';
import { useFormHandler } from '../hooks/useFormHandler';
import { useApiHandler } from '../hooks/useApiHandler';
import { DEFAULT_PAYLOAD, TICKET_LIST_PAGE_INFO, ticketAssignmentOptions } from '../constants/ticketlist.constants';
import {
    formatFilterValue,
    getItemFromSessionStorage,
    getTicketsSessionData,
    handleChange,
    setTicketsSessionData,
} from '../constants/ticketlist.utils';
import { IuseDefaultSearchProps } from '../constants/ticketlist.interface';

export const useDefaultSearch = (props: IuseDefaultSearchProps) => {
    const { isInitialLoad } = props;
    const dispatch = useAppDispatch();
    const { filteredListAPI } = useApiHandler();
    const [hasBeenModified, setHasBeenModified] = useState(false);
    const [tktAssignmentOptions, setTktAssignmentOptions] = useState(ticketAssignmentOptions);

    const { deafultSearchForm } = useFormHandler();
    const {
        handleSubmit,
        watch,
        setValue,
        reset,
        register,
        formState: { isDirty },
    } = deafultSearchForm;
    const formData = watch();

    useEffect(() => {
        const sessionData = getTicketsSessionData();
        let data = {
            userticketQueues: '',
            filterticketStatus: 'Open',
            userFilter: 'assignedToMeFilter',
            watchStatusBasic: 'all',
        };
        if (!isInitialLoad.current) {
            data = setTicketsSessionData(
                {
                    workgroup: sessionData.userticketQueues || '',
                    ticketStatus: sessionData.filterticketStatus || 'Open',
                    ticketAssignment: sessionData.userFilter || 'assignedToMeFilter',
                    watchStatusBasic: sessionData.watchStatusBasic || 'all',
                },
                true,
            );
        }
        setValue('workgroup', data.userticketQueues);
        setValue('ticketStatus', data.filterticketStatus);
        setValue('ticketAssignment', data.userFilter);
        setValue('watchStatusBasic', data.watchStatusBasic);
        if (data.filterticketStatus === 'UnClaimed') {
            const newTktAssignmentOptions = tktAssignmentOptions.filter(item => item.value !== 'assignedToMeFilter');
            setTktAssignmentOptions(newTktAssignmentOptions);
        }
    }, []);

    useEffect(() => {
        return () => {
            setTicketsSessionData(formData, true);
        };
    }, [formData]);

    useEffect(() => {
        if (!hasBeenModified && isDirty) {
            setHasBeenModified(true);
        }
    }, [isDirty, formData, hasBeenModified]);

    const fetchFilteredTickets = useCallback(
        async (formData: any) => {
            const data = setTicketsSessionData(formData, true);
            const {
                currentPage = 1,
                sortField = '',
                sortOrder = '',
            } = getItemFromSessionStorage(TICKET_LIST_PAGE_INFO) || {};
            const payload = {
                ...DEFAULT_PAYLOAD,
                filterticketStatus: formatFilterValue(data?.filterticketStatus) || '',
                userticketQueues: formatFilterValue(data?.userticketQueues) || '',
                userFilter: data?.userFilter || '',
                watchStatusBasic: data?.watchStatusBasic || '',
                pageNumber: hasBeenModified ? 1 : currentPage,
                sortBy: hasBeenModified ? '' : sortField,
                sortOrder: hasBeenModified ? '' : sortOrder,
                csrftoken: window.csrf_token,
            };
            await filteredListAPI(dispatch, payload);
        },
        [hasBeenModified],
    );

    const handleTicketAssignmentChange = e => {
        handleChange('ticketStatus', e, setValue);
        if (e === 'UnClaimed') {
            const newTktAssignmentOptions = tktAssignmentOptions.filter(item => item.value !== 'assignedToMeFilter');
            setTktAssignmentOptions(newTktAssignmentOptions);
            handleChange('ticketAssignment', 'myWorkgroupFilter', setValue);
        } else {
            setTktAssignmentOptions(ticketAssignmentOptions);
        }
    };

    return {
        handleSubmit,
        watch,
        setValue,
        reset,
        register,
        fetchFilteredTickets,
        tktAssignmentOptions,
        setTktAssignmentOptions,
        handleTicketAssignmentChange,
    };
};
